<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import MediaGrid from 'next/message/chips/MediaGrid.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES, ATTACHMENT_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const mediaAttachments = computed(() =>
  (attachments.value || []).filter(attachment =>
    [
      ATTACHMENT_TYPES.IMAGE,
      ATTACHMENT_TYPES.VIDEO,
      ATTACHMENT_TYPES.IG_REEL,
    ].includes(attachment.fileType)
  )
);

const otherAttachments = computed(() =>
  (attachments.value || []).filter(
    attachment =>
      ![
        ATTACHMENT_TYPES.IMAGE,
        ATTACHMENT_TYPES.VIDEO,
        ATTACHMENT_TYPES.IG_REEL,
      ].includes(attachment.fileType)
  )
);

const hasMediaAttachments = computed(() => mediaAttachments.value.length > 0);

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const bubbleClass = computed(() => {
  return hasMediaAttachments.value ? 'px-2 py-2' : 'px-4 py-3';
});

const contentGapClass = computed(() => {
  return hasMediaAttachments.value ? 'gap-2' : 'gap-3';
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble :class="bubbleClass" data-bubble-name="text">
    <div class="flex flex-col" :class="contentGapClass">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <MediaGrid
        v-if="mediaAttachments.length"
        class="mb-1"
        :attachments="mediaAttachments"
      />
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips
        v-if="otherAttachments.length"
        :attachments="otherAttachments"
        class="gap-2"
      />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
